import { useState, useEffect } from "react";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import axios from "axios";
import { Database, Download, Shield, FileArchive, Code2, FileText, HardDrive, CheckCircle2, Upload, AlertTriangle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBackup() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingDb, setDownloadingDb] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const token = localStorage.getItem("token");

  const refreshInfo = () => {
    axios
      .get(`${API}/admin/backup/info`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setInfo(r.data))
      .catch(() => toast.error("Erro ao carregar informações"));
  };

  useEffect(() => {
    refreshInfo();
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleDownload = async (full = true) => {
    const setBusy = full ? setDownloading : setDownloadingDb;
    setBusy(true);
    try {
      const url = full
        ? `${API}/admin/backup/download`
        : `${API}/admin/backup/database-only`;
      const r = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 600000,
      });
      const blob = new Blob([r.data], { type: "application/zip" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      link.download = m ? m[1] : `arrochaplay-${full ? "backup" : "database"}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Backup baixado com sucesso!");
    } catch (e) {
      toast.error("Erro ao gerar backup");
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) {
      toast.error("Selecione um arquivo .zip primeiro");
      return;
    }
    if (confirmText.trim().toUpperCase() !== "APAGAR E RESTAURAR") {
      toast.error('Digite exatamente "APAGAR E RESTAURAR" para confirmar');
      return;
    }
    setRestoring(true);
    setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const r = await axios.post(`${API}/admin/backup/restore?wipe=true`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setRestoreResult(r.data);
      toast.success("Banco restaurado com sucesso!");
      refreshInfo();
      setConfirmText("");
      setRestoreFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao restaurar backup");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center gap-3">
          <Shield size={28} className="text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Backup do Sistema</h1>
            <p className="text-zinc-400 text-sm">Cópia 100% fiel do site em produção. Permite restauração completa.</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-900/20 to-zinc-900 border border-emerald-500/30 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={20} className="text-emerald-400" />
            <h2 className="font-bold text-lg text-white">BACKUP 100% COMPLETO</h2>
            <span className="ml-auto text-[10px] uppercase font-bold bg-emerald-500 text-black px-2 py-1 rounded">Recomendado</span>
          </div>
          <p className="text-zinc-300 mb-4">
            Gera uma <strong className="text-emerald-400">cópia completa</strong> de tudo: banco de dados, código backend, código frontend, configurações e lista de mídias.
            Permite restaurar o sistema completo em poucos minutos.
          </p>

          {loading ? (
            <p className="text-zinc-500">Carregando...</p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                <Card icon={<Database size={18} />} label="Banco MongoDB" value={`${info?.total_docs || 0} docs`} sub={`${Object.keys(info?.collections || {}).length} coleções`} />
                <Card icon={<Code2 size={18} />} label="Código Backend" value="Python" sub="server.py + automation" />
                <Card icon={<Code2 size={18} />} label="Código Frontend" value="React" sub="src + public + config" />
                <Card icon={<FileText size={18} />} label="Documentação" value="PRD" sub="memory + readme" />
                <Card icon={<HardDrive size={18} />} label="Lista de mídias" value="paths" sub="para download separado" />
                <Card icon={<Shield size={18} />} label="Configurações" value=".env exemplo" sub="sanitizado" />
              </div>

              <button
                onClick={() => handleDownload(true)}
                disabled={downloading}
                data-testid="backup-download-full"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-wait text-black font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25"
              >
                <Download size={20} />
                {downloading ? "Gerando ZIP..." : "Baixar Backup Completo (100%)"}
              </button>
            </>
          )}
        </div>

        <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <FileArchive size={20} className="text-blue-400" />
            <h2 className="font-bold text-lg text-white">Backup Apenas do Banco</h2>
          </div>
          <p className="text-zinc-300 mb-4 text-sm">
            ZIP só com o dump do MongoDB (todas as coleções em JSON). Mais rápido. Use quando precisar apenas dos dados.
          </p>
          <button
            onClick={() => handleDownload(false)}
            disabled={downloadingDb}
            data-testid="backup-download-db"
            className="flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-400 disabled:opacity-60 text-white font-bold rounded-lg transition-all"
          >
            <Download size={16} />
            {downloadingDb ? "Gerando..." : "Baixar Banco (.zip)"}
          </button>
        </div>

        {/* RESTORE — Sincronização Preview → Produção */}
        <div className="bg-gradient-to-br from-red-900/30 to-zinc-900 border-2 border-red-500/40 rounded-2xl p-6" data-testid="restore-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={22} className="text-red-400" />
            <h2 className="font-bold text-lg text-white">Restaurar Backup (Sincronizar Preview → Produção)</h2>
            <span className="ml-auto text-[10px] uppercase font-bold bg-red-500 text-white px-2 py-1 rounded">Perigoso</span>
          </div>
          <div className="bg-red-950/40 border border-red-500/30 rounded-lg p-4 mb-5 text-sm text-red-100">
            <p className="font-bold mb-2">⚠️ ATENÇÃO — Esta operação:</p>
            <ul className="list-disc list-inside space-y-1 opacity-95">
              <li><strong>APAGA TODOS OS DADOS</strong> do banco atual (artigos, CDs, vídeos, artistas, usuários, plays, downloads, tudo).</li>
              <li>Substitui pelo conteúdo do ZIP enviado.</li>
              <li>Não pode ser desfeita. Faça um backup antes se tiver dúvida.</li>
              <li><strong>Use para sincronizar:</strong> baixe o ZIP no Preview → faça Deploy → faça Login na Produção → suba o ZIP aqui.</li>
            </ul>
          </div>

          <label className="block mb-4">
            <span className="text-sm font-bold text-zinc-300 mb-2 block">1. Selecione o ZIP do backup</span>
            <input
              type="file"
              accept=".zip"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              data-testid="restore-file-input"
              className="block w-full text-sm text-zinc-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-red-500/20 file:text-red-300 hover:file:bg-red-500/30 cursor-pointer"
            />
            {restoreFile && (
              <p className="mt-2 text-xs text-zinc-400">
                📦 {restoreFile.name} — {(restoreFile.size / 1024).toFixed(1)} KB
              </p>
            )}
          </label>

          <label className="block mb-4">
            <span className="text-sm font-bold text-zinc-300 mb-2 block">2. Digite <code className="bg-black/40 px-1.5 py-0.5 rounded text-red-400 font-mono">APAGAR E RESTAURAR</code> para confirmar</span>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="APAGAR E RESTAURAR"
              data-testid="restore-confirm-input"
              className="w-full px-4 py-3 bg-black border-2 border-red-500/40 rounded-lg text-white font-mono placeholder-zinc-600 focus:outline-none focus:border-red-500 transition-colors"
            />
          </label>

          <button
            onClick={handleRestore}
            disabled={restoring || !restoreFile || confirmText.trim().toUpperCase() !== "APAGAR E RESTAURAR"}
            data-testid="restore-execute-btn"
            className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all shadow-lg shadow-red-500/25"
          >
            {restoring ? <RefreshCw size={20} className="animate-spin" /> : <Upload size={20} />}
            {restoring ? "Restaurando... (pode levar alguns minutos)" : "APAGAR E RESTAURAR AGORA"}
          </button>

          {restoreResult && (
            <div className="mt-5 bg-emerald-950/40 border border-emerald-500/30 rounded-lg p-4 text-sm" data-testid="restore-result">
              <p className="font-bold text-emerald-300 mb-2">✓ Restauração concluída ({restoreResult.status})</p>
              <p className="text-zinc-300 mb-1">Coleções apagadas: <strong>{restoreResult.wiped?.length || 0}</strong></p>
              <p className="text-zinc-300 mb-2">Documentos restaurados:</p>
              <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
                {Object.entries(restoreResult.restored || {}).sort(([, a], [, b]) => b - a).map(([name, count]) => (
                  <div key={name} className="flex justify-between text-xs px-2 py-1 bg-black/40 rounded">
                    <span className="text-zinc-400 font-mono">{name}</span>
                    <span className="text-emerald-400 font-bold">{count.toLocaleString("pt-BR")}</span>
                  </div>
                ))}
              </div>
              {restoreResult.errors?.length > 0 && (
                <div className="mt-3 text-red-300">
                  <p className="font-bold">Erros:</p>
                  <ul className="list-disc list-inside text-xs">
                    {restoreResult.errors.map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {info && (
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6">
            <h3 className="font-bold text-white mb-3">Coleções do banco</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {Object.entries(info.collections).sort(([, a], [, b]) => b - a).map(([name, count]) => (
                <div key={name} className="flex items-center justify-between px-4 py-2 bg-black/40 rounded-lg">
                  <span className="text-zinc-300 font-mono text-xs">{name}</span>
                  <span className="font-bold text-emerald-400 text-sm">{count.toLocaleString("pt-BR")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-200">
          <p className="font-bold mb-1">⚠️ Importante</p>
          <ul className="list-disc list-inside space-y-1 opacity-90">
            <li>O backup NÃO inclui os arquivos de mídia (vídeos, áudios, imagens) — apenas a lista deles. Eles ficam no storage e devem ser baixados separadamente.</li>
            <li>Guarde o backup em local seguro. Ele contém todos os seus dados.</li>
            <li>O arquivo <code>.env</code> incluído está sanitizado (sem secrets) — você precisa repor as chaves ao restaurar.</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}

function Card({ icon, label, value, sub }) {
  return (
    <div className="bg-black/40 border border-white/5 rounded-lg p-3">
      <div className="flex items-center gap-2 text-emerald-400 mb-1">{icon}<span className="text-xs uppercase font-bold tracking-wider">{label}</span></div>
      <p className="font-bold text-white">{value}</p>
      <p className="text-xs text-zinc-500">{sub}</p>
    </div>
  );
}
